if (!customElements.get('coverax-product-card')) {
  customElements.define(
    'coverax-product-card',
    class CoveraxProductCard extends HTMLElement {
      constructor() {
        super();
        this.onPillClick = this.onPillClick.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onCartError = this.onCartError.bind(this);
        this.resetAddedState = this.resetAddedState.bind(this);
        this.onInfoToggleClick = this.onInfoToggleClick.bind(this);
        this.onPanelClick = this.onPanelClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      }

      connectedCallback() {
        this.variants = JSON.parse(this.dataset.variants || '[]');
        this.variantPrices = JSON.parse(this.dataset.variantPrices || '{}');
        this.showUvp = this.dataset.showUvp === 'true';
        this.netLabel = this.dataset.netLabel || 'netto';
        this.uvpLabel = this.dataset.uvpLabel || 'UVP';
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector('[name="id"]');
        this.priceContainer = this.querySelector('[data-price-container]');
        this.skuContainer = this.querySelector('[data-sku-container]');
        this.skuValue = this.querySelector('[data-sku-value]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitLabel = this.submitButton?.querySelector('[data-submit-label]');
        this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
        this.addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';
        this.addedLabel = this.dataset.addedLabel || 'Added';
        this.awaitingCartUpdate = false;
        this.submittedVariantId = null;
        this.addedResetTimeout = null;
        this.infoToggle = this.querySelector('[data-info-toggle]');
        this.infoPanel = this.querySelector('[data-info-panel]');
        this.infoScroll = this.querySelector('[data-info-scroll]');
        this.infoSku = this.querySelector('[data-info-sku]');
        this.infoCloseTimeout = null;
        this.panel = this.querySelector('.coverax-product-card__panel');
        this.panelLink = this.querySelector('.coverax-product-card__panel-link');
        this.productUrl = this.dataset.productUrl || this.panelLink?.href || '';

        this.querySelectorAll('[data-option-pill]').forEach((pill) => {
          pill.addEventListener('click', this.onPillClick);
        });

        this.infoToggle?.addEventListener('click', this.onInfoToggleClick);
        this.panel?.addEventListener('click', this.onPanelClick);

        this.form?.addEventListener('submit', this.onFormSubmit);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError);
        }
      }

      disconnectedCallback() {
        this.querySelectorAll('[data-option-pill]').forEach((pill) => {
          pill.removeEventListener('click', this.onPillClick);
        });

        this.form?.removeEventListener('submit', this.onFormSubmit);
        this.infoToggle?.removeEventListener('click', this.onInfoToggleClick);
        this.panel?.removeEventListener('click', this.onPanelClick);
        this.closeInfoPanel(false);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.clearAddedResetTimeout();
        this.clearInfoCloseTimeout();
      }

      onInfoToggleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.classList.contains('is-info-open')) {
          this.closeInfoPanel();
          return;
        }

        this.closest('.coverax-product-cards')?.querySelectorAll('coverax-product-card.is-info-open').forEach((card) => {
          if (card !== this) card.closeInfoPanel();
        });

        this.openInfoPanel();
      }

      onPanelClick(event) {
        if (
          event.target.closest(
            '[data-option-pill], product-form, .coverax-product-card__submit, .coverax-product-card__form'
          )
        ) {
          return;
        }

        const url = this.productUrl || this.panelLink?.href;
        if (!url) return;

        event.preventDefault();
        window.location.assign(url);
      }

      onDocumentClick(event) {
        if (!this.classList.contains('is-info-open')) return;
        if (this.contains(event.target)) return;
        this.closeInfoPanel();
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape' || !this.classList.contains('is-info-open')) return;
        this.closeInfoPanel();
      }

      openInfoPanel() {
        if (!this.infoPanel || !this.infoToggle) return;

        this.classList.remove('is-info-closing');
        this.resetInfoScroll();

        this.infoPanel.hidden = false;
        this.infoToggle.setAttribute('aria-expanded', 'true');
        this.infoToggle.setAttribute(
          'aria-label',
          this.infoToggle.dataset.infoLabelClose || 'Close'
        );
        window.requestAnimationFrame(() => {
          this.classList.add('is-info-open');
          this.resetInfoScroll();
        });
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      resetInfoScroll() {
        if (this.infoScroll) {
          this.infoScroll.scrollTop = 0;
        }
      }

      closeInfoPanel(animate = true) {
        if (!this.infoPanel || !this.infoToggle) return;
        if (!this.classList.contains('is-info-open') && animate) return;

        this.infoToggle.setAttribute('aria-expanded', 'false');
        this.infoToggle.setAttribute(
          'aria-label',
          this.infoToggle.dataset.infoLabelOpen || 'Product information'
        );
        this.infoToggle.blur();
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);

        this.clearInfoCloseTimeout();

        if (!animate) {
          this.classList.remove('is-info-open', 'is-info-closing');
          this.infoPanel.hidden = true;
          this.resetInfoScroll();
          return;
        }

        this.classList.add('is-info-closing');
        this.classList.remove('is-info-open');

        this.infoCloseTimeout = window.setTimeout(() => {
          this.classList.remove('is-info-closing');
          if (!this.classList.contains('is-info-open')) {
            this.infoPanel.hidden = true;
            this.resetInfoScroll();
          }
        }, 420);
      }

      clearInfoCloseTimeout() {
        if (this.infoCloseTimeout) {
          window.clearTimeout(this.infoCloseTimeout);
          this.infoCloseTimeout = null;
        }
      }

      onFormSubmit() {
        if (!this.variantInput) return;

        this.awaitingCartUpdate = true;
        this.submittedVariantId = this.variantInput.value;
        this.resetAddedState();
      }

      onCartUpdate(event) {
        if (!this.awaitingCartUpdate || event.source !== 'product-form') return;
        if (String(event.productVariantId) !== String(this.submittedVariantId)) return;
        if (!this.form) return;

        this.awaitingCartUpdate = false;
        this.showAddedState();
      }

      onCartError(event) {
        if (event.source !== 'product-form') return;
        if (String(event.productVariantId) !== String(this.submittedVariantId)) return;

        this.awaitingCartUpdate = false;
      }

      showAddedState() {
        if (!this.submitButton || this.submitButton.disabled) return;

        this.submitButton.classList.add('is-added');

        if (this.submitLabel) {
          this.submitLabel.textContent = this.addedLabel;
        }

        this.clearAddedResetTimeout();
        this.addedResetTimeout = window.setTimeout(this.resetAddedState, 2500);
      }

      resetAddedState() {
        this.clearAddedResetTimeout();
        if (!this.submitButton) return;

        this.submitButton.classList.remove('is-added');

        if (this.submitLabel && !this.submitButton.disabled) {
          this.submitLabel.textContent = this.addToCartLabel;
        }
      }

      clearAddedResetTimeout() {
        if (this.addedResetTimeout) {
          window.clearTimeout(this.addedResetTimeout);
          this.addedResetTimeout = null;
        }
      }

      getSelectedOptions() {
        const options = [];

        this.querySelectorAll('[data-option-index]').forEach((group) => {
          const activePill = group.querySelector('[data-option-pill].is-active');
          if (activePill) {
            options[Number(group.dataset.optionIndex)] = activePill.dataset.optionValue;
          }
        });

        return options;
      }

      findVariant(options) {
        return this.variants.find((variant) => {
          return variant.options.every((value, index) => value === options[index]);
        });
      }

      onPillClick(event) {
        const pill = event.currentTarget;
        if (pill.disabled || pill.classList.contains('is-active')) return;

        const group = pill.closest('[data-option-index]');
        group.querySelectorAll('[data-option-pill]').forEach((item) => {
          item.classList.toggle('is-active', item === pill);
        });

        this.updateVariant();
      }

      renderPriceHtml(variantId) {
        const priceData = this.variantPrices[String(variantId)];
        if (!priceData) return '';

        const compareAtValue = Number(priceData.compare_at_value) || 0;
        const priceValue = Number(priceData.price_value) || 0;
        const showUvpLine = this.showUvp && compareAtValue > 0 && compareAtValue > priceValue;

        if (this.showUvp && !showUvpLine) {
          return `
            <div class="coverax-product-card__price-display">
              <div class="coverax-product-card__price-stack coverax-product-card__price-stack--guest">
                <span class="coverax-product-card__price-amount" data-price-amount>${priceData.price}</span>
                <span class="coverax-product-card__price-meta">
                  <span>${this.uvpLabel}</span>
                  <span class="coverax-product-card__price-meta-sep" aria-hidden="true">·</span>
                  <span>${this.netLabel}</span>
                </span>
              </div>
            </div>
          `;
        }

        const uvpStack = showUvpLine
          ? `
              <div class="coverax-product-card__price-stack coverax-product-card__price-stack--uvp">
                <span class="coverax-product-card__price-amount coverax-product-card__price-amount--uvp" data-price-uvp>${priceData.compare_at}</span>
                <span class="coverax-product-card__price-meta">${this.uvpLabel}</span>
              </div>
            `
          : '';

        const netStack =
          !this.showUvp || showUvpLine
            ? `
              <div class="coverax-product-card__price-stack coverax-product-card__price-stack--net">
                <span class="coverax-product-card__price-amount" data-price-amount>${priceData.price}</span>
                <span class="coverax-product-card__price-meta">${this.netLabel}</span>
              </div>
            `
            : '';

        return `
          <div class="coverax-product-card__price-display">
            ${uvpStack}
            ${netStack}
          </div>
        `;
      }

      updateVariant() {
        const options = this.getSelectedOptions();
        const variant = this.findVariant(options);

        if (!variant || !this.variantInput) return;

        this.resetAddedState();
        this.awaitingCartUpdate = false;
        this.submittedVariantId = null;

        this.variantInput.value = variant.id;
        this.variantInput.disabled = !variant.available;

        this.querySelectorAll('[data-option-pill]').forEach((pill) => {
          const group = pill.closest('[data-option-index]');
          const optionIndex = Number(group.dataset.optionIndex);
          const testOptions = [...options];
          testOptions[optionIndex] = pill.dataset.optionValue;
          const testVariant = this.findVariant(testOptions);
          pill.disabled = !testVariant || !testVariant.available;
        });

        if (this.priceContainer) {
          this.priceContainer.innerHTML = this.renderPriceHtml(variant.id);
        }

        if (this.skuContainer && this.skuValue) {
          if (variant.sku) {
            this.skuValue.textContent = variant.sku;
            this.skuContainer.hidden = false;
            if (this.infoSku && !this.infoSku.hasAttribute('data-info-demo')) {
              this.infoSku.textContent = variant.sku;
            }
          } else {
            this.skuContainer.hidden = true;
            if (this.infoSku && !this.infoSku.hasAttribute('data-info-demo')) {
              this.infoSku.textContent = '';
            }
          }
        }

        if (this.submitButton) {
          this.submitButton.disabled = !variant.available;
          if (this.submitLabel) {
            this.submitLabel.textContent = variant.available ? this.addToCartLabel : this.soldOutLabel;
          }
        }
      }
    }
  );
}
